/**
 * Capacity Analysis Component
 * Displays resource capacity analysis
 */
import { useState, useEffect } from 'react'
import { Users, AlertTriangle, CheckCircle } from 'lucide-react'
import { programResourcesService, CapacityAnalysis, CapacityAnalysisItem } from '../../services/programResources'
import { toast } from 'sonner'
import { getErrorMessage } from '../../utils/apiTransformers'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import type { ApiErrorLike } from '../../utils/apiError'

interface CapacityAnalysisProps {
  programId: number
}

export default function CapacityAnalysisComponent({ programId }: CapacityAnalysisProps) {
  const [analysis, setAnalysis] = useState<CapacityAnalysis | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadAnalysis()
  }, [programId])

  const loadAnalysis = async () => {
    setLoading(true)
    try {
      const data = await programResourcesService.getCapacityAnalysis(programId)
      setAnalysis(data)
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      console.error('Error loading capacity analysis:', error)
      toast.error(getErrorMessage(error) || 'Failed to load capacity analysis')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-xl border-2 border-[#bcccdc] shadow-md p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-[#f0f4f8] rounded w-1/3 mb-4"></div>
          <div className="h-64 bg-[#f0f4f8] rounded"></div>
        </div>
      </div>
    )
  }

  if (!analysis || !analysis.capacity_analysis.length) {
    return (
      <div className="bg-white rounded-xl border-2 border-[#bcccdc] shadow-md p-6">
        <h3 className="text-xl font-semibold text-[#102a43] mb-4">Capacity Analysis</h3>
        <p className="text-[#829ab1]">No capacity data available</p>
      </div>
    )
  }

  const chartData = analysis.capacity_analysis.map(ca => ({
    name: ca.role_name,
    required: ca.required_count,
    allocated: ca.allocated_count,
    gap: Math.abs(ca.gap)
  }))

  const shortages = analysis.capacity_analysis.filter((ca: CapacityAnalysisItem) => ca.status === 'shortage')
  const surpluses = analysis.capacity_analysis.filter((ca: CapacityAnalysisItem) => ca.status === 'surplus')

  return (
    <div className="bg-white rounded-xl border-2 border-[#bcccdc] shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Users className="w-6 h-6 text-[#486581]" />
          <h3 className="text-xl font-semibold text-[#102a43]">Capacity Analysis</h3>
        </div>
        <div className="flex items-center gap-4 text-sm">
          {shortages.length > 0 && (
            <div className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-4 h-4" />
              <span>{shortages.length} shortages</span>
            </div>
          )}
          {surpluses.length > 0 && (
            <div className="flex items-center gap-2 text-green-600">
              <CheckCircle className="w-4 h-4" />
              <span>{surpluses.length} surpluses</span>
            </div>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="p-4 bg-[#f0f4f8] rounded-lg">
          <div className="text-sm text-[#829ab1] mb-1">Total Required</div>
          <div className="text-2xl font-bold text-[#102a43]">{analysis.total_required}</div>
        </div>
        <div className="p-4 bg-[#f0f4f8] rounded-lg">
          <div className="text-sm text-[#829ab1] mb-1">Total Allocated</div>
          <div className="text-2xl font-bold text-[#102a43]">{analysis.total_allocated}</div>
        </div>
        <div className="p-4 bg-[#f0f4f8] rounded-lg">
          <div className="text-sm text-[#829ab1] mb-1">Total Gap</div>
          <div className={`text-2xl font-bold ${analysis.total_gap > 0 ? 'text-red-600' : analysis.total_gap < 0 ? 'text-green-600' : 'text-[#102a43]'}`}>
            {analysis.total_gap > 0 ? '+' : ''}{analysis.total_gap}
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="mb-6">
        <h4 className="text-sm font-semibold text-[#102a43] mb-4">Capacity by Role</h4>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis 
              dataKey="name" 
              stroke="#829ab1"
              style={{ fontSize: '12px' }}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis 
              stroke="#829ab1"
              style={{ fontSize: '12px' }}
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#fff', 
                border: '1px solid #bcccdc',
                borderRadius: '8px'
              }}
            />
            <Bar 
              dataKey="required" 
              fill="#bcccdc" 
              name="Required"
              radius={[4, 4, 0, 0]}
            />
            <Bar 
              dataKey="allocated" 
              fill="#486581" 
              name="Allocated"
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Detailed List */}
      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-[#102a43]">Role Details</h4>
        {analysis.capacity_analysis.map((ca: CapacityAnalysisItem) => (
          <div
            key={ca.role_name}
            className="p-4 border border-[#bcccdc] rounded-lg"
          >
            <div className="flex items-center justify-between mb-2">
              <h5 className="font-medium text-[#102a43]">{ca.role_name}</h5>
              <span className={`px-2 py-0.5 text-xs rounded border ${
                ca.status === 'adequate' ? 'bg-green-100 text-green-700 border-green-200' :
                ca.status === 'shortage' ? 'bg-red-100 text-red-700 border-red-200' :
                'bg-yellow-100 text-yellow-700 border-yellow-200'
              }`}>
                {ca.status.replace('_', ' ')}
              </span>
            </div>
            <div className="grid grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-[#829ab1]">Required:</span>
                <span className="ml-2 font-medium text-[#102a43]">{ca.required_count}</span>
              </div>
              <div>
                <span className="text-[#829ab1]">Allocated:</span>
                <span className="ml-2 font-medium text-[#102a43]">{ca.allocated_count}</span>
              </div>
              <div>
                <span className="text-[#829ab1]">Open:</span>
                <span className="ml-2 font-medium text-[#102a43]">{ca.open_positions}</span>
              </div>
              <div>
                <span className="text-[#829ab1]">Gap:</span>
                <span className={`ml-2 font-medium ${ca.gap > 0 ? 'text-red-600' : ca.gap < 0 ? 'text-green-600' : 'text-[#102a43]'}`}>
                  {ca.gap > 0 ? '+' : ''}{ca.gap}
                </span>
              </div>
            </div>
            <div className="mt-2 w-full h-2 bg-[#f0f4f8] rounded-full">
              <div
                className={`h-2 rounded-full ${
                  ca.utilization_rate >= 100 ? 'bg-green-500' :
                  ca.utilization_rate >= 80 ? 'bg-yellow-500' :
                  'bg-red-500'
                }`}
                style={{ width: `${Math.min(100, ca.utilization_rate)}%` }}
              />
              <div className="text-xs text-[#829ab1] mt-1">
                Utilization: {ca.utilization_rate.toFixed(1)}%
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

